import { useEffect, useRef, useState } from "react";
import {
  useGetIdentity, // GAVION batch 3: emails
  useGetMany,
  useInfiniteGetList,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  dedupeEmailItems, // GAVION batch 3: emails
  TIMELINE_CATEGORIES,
  type TimelineCategory,
  type TimelineItem,
} from "../../domain/timeline";
import { mergeTimelinePages } from "./leadShowUtils";
import { TimelineEntry } from "./TimelineEntry";

type Chip = "all" | TimelineCategory;
const CHIPS: readonly Chip[] = ["all", ...TIMELINE_CATEGORIES];
const PAGE_SIZE = 50;

const LoadingSkeleton = ({
  label,
  rows = 3,
}: {
  label: string;
  rows?: number;
}) => (
  <div role="status" className="flex flex-col gap-3" aria-busy="true">
    <span className="sr-only">{label}</span>
    {Array.from({ length: rows }, (_, i) => (
      <Skeleton key={i} className="h-16" />
    ))}
  </div>
);

export const LeadTimeline = ({ companyId }: { companyId: Identifier }) => {
  const translate = useTranslate();
  const [chip, setChip] = useState<Chip>("all");
  const filter =
    chip === "all"
      ? { company_id: companyId }
      : { company_id: companyId, category: chip };
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isPending,
    error,
    refetch,
  } = useInfiniteGetList<TimelineItem>("v_timeline", {
    filter,
    sort: { field: "at", order: "DESC" },
    pagination: { page: 1, perPage: PAGE_SIZE },
  });
  // GAVION batch 3: emails (one entry per internet message)
  const { identity } = useGetIdentity();
  const items = data
    ? dedupeEmailItems(
        mergeTimelinePages(data.pages),
        identity?.id === undefined ? null : Number(identity.id),
      )
    : [];
  const salesIds = [
    ...new Set(
      items.map((i) => i.sales_id).filter((id): id is number => id !== null),
    ),
  ];
  const { data: sales = [] } = useGetMany(
    "sales",
    { ids: salesIds },
    { enabled: salesIds.length > 0 },
  );
  const salesName = new Map(
    sales.map((s) => [
      String(s.id),
      [s.first_name, s.last_name].filter(Boolean).join(" "),
    ]),
  );

  // Infinite scroll; paused after a next-page error until the user retries.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (
      !el ||
      !hasNextPage ||
      isFetchNextPageError ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage)
        fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  return (
    <section
      aria-labelledby="lead-timeline-title"
      className="flex flex-col gap-4 rounded-xl border bg-card p-4"
    >
      <h2 id="lead-timeline-title" className="text-lg font-bold">
        {translate("gavion.timeline.title")}
      </h2>
      <div
        role="group"
        aria-label={translate("gavion.leads.filters.title")}
        className="flex flex-wrap gap-2"
      >
        {CHIPS.map((c) => (
          <Button
            key={c}
            size="sm"
            className="rounded-full"
            variant={c === chip ? "default" : "outline"}
            aria-pressed={c === chip}
            onClick={() => setChip(c)}
          >
            {translate(`gavion.timeline.filters.${c}`)}
          </Button>
        ))}
      </div>
      {isPending ? (
        <LoadingSkeleton label={translate("ra.page.loading")} />
      ) : error && !data ? (
        <div role="alert" className="flex items-center gap-3 text-sm">
          <span>{translate("gavion.timeline.error")}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {translate("gavion.timeline.retry")}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("gavion.timeline.empty")}
        </p>
      ) : (
        <ol className="flex flex-col gap-3">
          {items.map((item) => (
            <TimelineEntry
              key={item.id}
              item={item}
              salesName={
                item.sales_id === null
                  ? null
                  : (salesName.get(String(item.sales_id)) ?? null)
              }
            />
          ))}
        </ol>
      )}
      {data && isFetchNextPageError ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-center gap-3 text-sm"
        >
          <span>{translate("gavion.timeline.error")}</span>
          <Button variant="outline" size="sm" onClick={() => fetchNextPage()}>
            {translate("gavion.timeline.retry")}
          </Button>
        </div>
      ) : hasNextPage ? (
        <div ref={sentinel} className="flex justify-center">
          <Button
            variant="ghost"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {translate("gavion.timeline.load_more")}
          </Button>
        </div>
      ) : null}
    </section>
  );
};
